/**
 * Inventário de rotas — fonte única para o roteador e para os testes E2E de layout.
 *
 * Existe porque os specs de layout (`e2e/responsive.spec.ts`, `e2e/layout/tap-targets.spec.ts`)
 * precisam varrer TODAS as rotas alcançáveis. Enquanto a lista vivia escrita à mão dentro do
 * spec, ela cobria 3 de 5 rotas e apodrecia no primeiro marco seguinte: quem acrescenta uma rota
 * não tem motivo para lembrar de um arquivo em `e2e/`. Aqui, o mesmo módulo que o roteador
 * consome alimenta os specs — acrescentar rota sem cobrir é uma falha de teste, não um descuido.
 *
 * `routes-manifest.test.ts` garante que manifesto e `app-router.tsx` não divergem. Sem esse
 * teste isto seria a mesma lista manual com outro nome.
 */

/** Superfícies onde uma rota pode existir. A landing não entra no bundle nativo (PRD §4.6.4). */
export type Superficie = 'web' | 'native';

export interface RotaManifesto {
  /** Padrão do React Router, tal como aparece em `<Route path>`. */
  path: string;
  /** Onde a rota é alcançável. */
  superficies: readonly Superficie[];
  /** `true` quando a rota exige sessão; `false` quando é pública; `'ambos'` quando serve os dois. */
  auth: boolean | 'ambos';
  /**
   * Caminho concreto para navegar num teste E2E. Difere do `path` quando este tem splat (`/*`)
   * ou curinga — `page.goto('/home/*')` não é um URL.
   */
  e2e: string | null;
}

export const ROTAS: readonly RotaManifesto[] = [
  // Raiz: landing para visitante web, shell para autenticado, login em nativo.
  { path: '/', superficies: ['web', 'native'], auth: 'ambos', e2e: '/' },
  { path: '/welcome', superficies: ['web', 'native'], auth: false, e2e: '/welcome' },
  { path: '/login', superficies: ['web', 'native'], auth: false, e2e: '/login' },
  { path: '/signup', superficies: ['web', 'native'], auth: false, e2e: '/signup' },
  { path: '/password-recovery', superficies: ['web', 'native'], auth: false, e2e: '/password-recovery' },
  { path: '/reset-password', superficies: ['web', 'native'], auth: false, e2e: '/reset-password' },
  { path: '/auth/callback', superficies: ['web', 'native'], auth: false, e2e: '/auth/callback' },
  // O signup-guard cria a identidade sem sessão no cliente. O recibo efêmero permite ao visitante
  // pendente usar esta rota; uma sessão recém-confirmada também pode atravessá-la.
  { path: '/verify', superficies: ['web', 'native'], auth: 'ambos', e2e: '/verify' },
  { path: '/legal/:kind', superficies: ['web', 'native'], auth: false, e2e: '/legal/terms_consumer' },
  // Onboarding consumer (T1.14): exige sessão — a identidade já existe quando se chega aqui.
  { path: '/onboarding/consumer', superficies: ['web', 'native'], auth: true, e2e: '/onboarding/consumer' },
  { path: '/home/*', superficies: ['web', 'native'], auth: true, e2e: '/home' },
  { path: '/home/busca', superficies: ['web', 'native'], auth: true, e2e: '/home/busca' },
  { path: '/pro/:slug', superficies: ['web', 'native'], auth: true, e2e: '/pro/perfil-em-preparacao' },
  { path: '/dashboard/*', superficies: ['web', 'native'], auth: true, e2e: '/dashboard' },
  // Onboarding profissional (T1.15). Está coberto pelo splat acima para efeitos de roteamento,
  // mas é declarado à parte porque o `e2e` do splat aponta para `/dashboard` — sem esta linha a
  // tela das 7 etapas nasceria sem cobertura de overflow nem de alvo de toque, que é exatamente
  // o que este manifesto existe para impedir.
  {
    path: '/dashboard/onboarding',
    superficies: ['web', 'native'],
    auth: true,
    e2e: '/dashboard/onboarding',
  },
  // Exclusão de conta (T1.17, App Store 5.1.1(v)): tela que a App Review abre, com cobertura
  // própria de overflow e alvo de toque — os splats acima só levam o E2E às raízes dos shells. As
  // bases (`/home/perfil`, `/dashboard/configuracoes`) nascem de `nav-items.ts`, não de `<Route>`
  // literal; o layout delas é coberto por `e2e/account/deletion.spec.ts`.
  {
    path: '/home/perfil/eliminar-conta',
    superficies: ['web', 'native'],
    auth: true,
    e2e: '/home/perfil/eliminar-conta',
  },
  {
    path: '/dashboard/configuracoes/eliminar-conta',
    superficies: ['web', 'native'],
    auth: true,
    e2e: '/dashboard/configuracoes/eliminar-conta',
  },
  // Catch-all. Em nativo não-autenticado converge para o login; no resto, 404.
  // O `e2e` usa um caminho concreto porque `'*'` não é navegável.
  { path: '*', superficies: ['web', 'native'], auth: 'ambos', e2e: '/rota-inexistente' },
] as const;

/**
 * Caminhos navegáveis por um teste E2E, sem duplicados.
 *
 * 🔴 Não filtra por autenticação: uma rota protegida redireciona para o login, e o ecrã de destino
 * também precisa de passar no piso de 44px e no teste de overflow. Filtrar aqui deixaria o
 * redirecionamento sem cobertura.
 */
export const rotasE2E = (): string[] => [
  ...new Set(ROTAS.map((r) => r.e2e).filter((p): p is string => p !== null)),
];

/** Rotas públicas concretas; usadas pelos gates que não fabricam sessão de teste. */
export const rotasPublicasE2E = (): string[] => [
  ...new Set(
    ROTAS.filter((r) => r.auth !== true)
      .map((r) => r.e2e)
      .filter((p): p is string => p !== null),
  ),
];

/**
 * Rotas privadas concretas (T1.19): o visitante anónimo nunca as pode ver. Alimenta o
 * `e2e/auth/private-routes.spec.ts` — rota privada nova nasce com a verificação de proteção.
 */
export const rotasPrivadasE2E = (): string[] => [
  ...new Set(
    ROTAS.filter((r) => r.auth === true)
      .map((r) => r.e2e)
      .filter((p): p is string => p !== null),
  ),
];

/** Superfícies públicas da jornada de autenticação da T1.13. */
export const rotasAuthE2E = (): string[] =>
  ['/welcome', '/login', '/signup', '/verify', '/auth/callback'];

/** Padrões declarados para uma superfície — usado pelo teste que compara manifesto e roteador. */
export const pathsDaSuperficie = (s: Superficie): string[] =>
  ROTAS.filter((r) => r.superficies.includes(s)).map((r) => r.path);
